/**
 * TanStack Query hooks for `campaign-wizard` — wrap the four server calls in
 * api.ts. Every hook invalidates the `all` key on success so list/detail
 * consumers see fresh server state after mutations.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	createDraft,
	finalizeDraft,
	getDraft,
	saveStep,
	campaignWizardQueryKeys,
	type CampaignDraftRow,
	type FinalizeResponse,
	type StepSaveResponse,
	type WizardStep,
} from "./api";

export function useCampaignWizardDraft(
	id: string | null,
): UseQueryResult<CampaignDraftRow, Error> {
	return useQuery({
		queryKey: id
			? campaignWizardQueryKeys.detail(id)
			: [...campaignWizardQueryKeys.all, "detail", "none"],
		queryFn: () => getDraft(id as string),
		enabled: Boolean(id),
	});
}

export function useCreateCampaignDraft(): UseMutationResult<
	CampaignDraftRow,
	Error,
	void
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => createDraft(),
		onSuccess: (row) => {
			qc.setQueryData(campaignWizardQueryKeys.detail(row.id), row);
			qc.invalidateQueries({ queryKey: campaignWizardQueryKeys.all });
		},
	});
}

export interface SaveStepArgs {
	id: string;
	step: WizardStep;
	payload: unknown;
}

export function useSaveCampaignStep(): UseMutationResult<
	StepSaveResponse,
	Error,
	SaveStepArgs
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (args: SaveStepArgs) => saveStep(args),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({
				queryKey: campaignWizardQueryKeys.detail(vars.id),
			});
		},
	});
}

export function useFinalizeCampaignDraft(): UseMutationResult<
	FinalizeResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => finalizeDraft(id),
		onSuccess: (_data, id) => {
			qc.invalidateQueries({
				queryKey: campaignWizardQueryKeys.detail(id),
			});
			qc.invalidateQueries({ queryKey: campaignWizardQueryKeys.all });
		},
	});
}
